import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { openFinanceService } from '@services/openFinanceService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'open-finance-connections';

export const useOpenFinance = () => {
  const queryClient = useQueryClient();

  const {
    data: connections = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY],
    queryFn: openFinanceService.getConnections,
  });

  const connectTokenMutation = useMutation({
    mutationFn: openFinanceService.createConnectToken,
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao gerar token de conexao');
    },
  });

  const processConnectionMutation = useMutation({
    mutationFn: openFinanceService.processConnection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Banco conectado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao processar conexao');
    },
  });

  const syncAccountsMutation = useMutation({
    mutationFn: (connectionId) => openFinanceService.syncAccounts(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Contas sincronizadas!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao sincronizar contas');
    },
  });

  const syncTransactionsMutation = useMutation({
    mutationFn: ({ connectionId, data }) => openFinanceService.syncTransactions(connectionId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(`${result.created} transacoes importadas!`);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao sincronizar transacoes');
    },
  });

  const linkAccountMutation = useMutation({
    mutationFn: ({ connectionId, accountId }) => openFinanceService.linkAccount(connectionId, accountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Conta vinculada!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao vincular conta');
    },
  });

  const deleteConnectionMutation = useMutation({
    mutationFn: openFinanceService.deleteConnection,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Conexao removida!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao remover conexao');
    },
  });

  const activeConnections = connections.filter(c => c.status === 'active');
  const pendingConnections = connections.filter(c => c.status === 'pending');

  return {
    connections,
    activeConnections,
    pendingConnections,
    isLoading,
    error,
    refetch,
    createConnectToken: connectTokenMutation.mutateAsync,
    processConnection: processConnectionMutation.mutateAsync,
    syncAccounts: syncAccountsMutation.mutateAsync,
    syncTransactions: syncTransactionsMutation.mutateAsync,
    linkAccount: linkAccountMutation.mutateAsync,
    deleteConnection: deleteConnectionMutation.mutateAsync,
    isCreatingToken: connectTokenMutation.isPending,
    isProcessing: processConnectionMutation.isPending,
    isSyncingAccounts: syncAccountsMutation.isPending,
    isSyncingTransactions: syncTransactionsMutation.isPending,
    isLinking: linkAccountMutation.isPending,
    isDeleting: deleteConnectionMutation.isPending,
  };
};

export default useOpenFinance;
